import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './MarkitdownPage.css';
import './EuFulfillmentPage.css';

const STATUS = {
  ready:   { label: 'Ready',             cls: 'status-ok' },
  partial: { label: 'Partial',           cls: 'status-warning' },
  done:    { label: 'Already fulfilled', cls: 'status-none' },
  blocked: { label: 'Blocked',           cls: 'status-critical' },
};

export default function EuFulfillmentPage() {
  const [file,     setFile]     = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [plan,     setPlan]     = useState(null);   // preview response
  const [notify,   setNotify]   = useState(false);
  const [record,   setRecord]   = useState(null);   // status record while/after applying
  const [history,  setHistory]  = useState([]);
  const fileInputRef = useRef();
  const pollRef = useRef();

  function loadHistory() {
    axios.get('/api/eu-fulfill/history').then(r => setHistory(r.data)).catch(() => {});
  }
  useEffect(() => { loadHistory(); return () => clearInterval(pollRef.current); }, []);

  function pick(files) {
    const f = Array.from(files).find(f => /\.(csv|xlsx)$/i.test(f.name));
    if (!f) { setError('Choose the CSV (or .xlsx) report that was sent to Fanfinity.'); return; }
    setFile(f); setPlan(null); setRecord(null); setError(null);
  }

  async function handlePreview() {
    if (!file) return;
    setLoading(true); setError(null); setPlan(null); setRecord(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await axios.post('/api/eu-fulfill/preview', form);
      setPlan(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleApply() {
    const n = plan.counts.ready + plan.counts.partial;
    if (!window.confirm(`Mark ${n} order(s) / ${plan.units} unit(s) fulfilled in Shopify?` +
                        (notify ? '\n\nCustomers WILL be emailed a shipping confirmation.' : ''))) return;
    setError(null);
    try {
      await axios.post(`/api/eu-fulfill/apply/${plan.id}`, { notify_customer: notify });
      setRecord({ state: 'applying' });
      pollRef.current = setInterval(async () => {
        try {
          const r = await axios.get(`/api/eu-fulfill/status/${plan.id}`);
          if (r.data.state !== 'applying') {
            clearInterval(pollRef.current);
            setRecord(r.data);
            loadHistory();
          }
        } catch (e) { /* keep polling */ }
      }, 2000);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }

  function reset() {
    setFile(null); setPlan(null); setRecord(null); setError(null); setNotify(false);
  }

  const fulfillable = plan ? plan.counts.ready + plan.counts.partial : 0;
  const res = record?.results;

  return (
    <div className="mk-page euf-page">
      <h1>EU Fulfillment — Mark Fanfinity Orders Fulfilled</h1>
      <p className="mk-sub">
        Once Fanfinity confirms they've shipped a weekly list, upload that same report here
        (<code>heavyplay_eu_eu_&lt;date&gt;.csv</code>). Each line is matched by SKU to the order's open
        Fanfinity BV fulfillment and marked fulfilled for exactly the file's quantity. Lines that are
        already fulfilled are skipped, so uploading a file twice is safe. A summary is posted to Slack.
      </p>

      {!plan && (
        <>
          <div
            className={`mk-dropzone${dragging ? ' mk-dropzone--active' : ''}`}
            onClick={() => fileInputRef.current.click()}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files); }}
          >
            <div className="mk-dropzone-icon">📦</div>
            <p className="mk-dropzone-label">
              {file ? file.name : <>Drop the Fanfinity report here or <span className="mk-link">browse</span></>}
            </p>
            <p className="mk-dropzone-hint">CSV or .xlsx</p>
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx" style={{ display: 'none' }}
                   onChange={e => { pick(e.target.files); e.target.value = ''; }} />
          </div>
          <div className="mk-actions">
            <button className="mk-btn-primary" disabled={!file || loading} onClick={handlePreview}>
              {loading ? 'Checking orders in Shopify…' : 'Preview'}
            </button>
          </div>
        </>
      )}

      {error && <div className="mk-error">{error}</div>}

      {plan && (
        <div className="mk-results">
          <h2 className="mk-section-title">{plan.file}</h2>
          <div className="euf-counts">
            {Object.entries(STATUS).map(([k, s]) => (
              <span key={k} className="euf-count"><b className={s.cls}>{plan.counts[k]}</b> {s.label}</span>
            ))}
            <span className="euf-count"><b>{plan.units}</b> units to fulfill</span>
          </div>
          {plan.notes.map((n, i) => <p key={i} className="euf-note">{n}</p>)}

          <div className="table-wrap">
            <table>
              <thead><tr><th>Order</th><th>Status</th><th>SKU</th><th>In file</th><th>Will fulfill</th><th>Notes</th></tr></thead>
              <tbody>
                {plan.orders.map(o => (o.lines.length ? o.lines : [null]).map((l, i) => (
                  <tr key={o.order + i}>
                    <td>{i === 0 ? o.order : ''}</td>
                    <td>{i === 0 && <span className={STATUS[o.status].cls}>{STATUS[o.status].label}</span>}</td>
                    <td>{l ? (l.sku || l.name) : '—'}</td>
                    <td>{l ? l.file_qty : ''}</td>
                    <td>{l ? l.fulfill_qty : ''}</td>
                    <td className="euf-notes">{i === 0 ? o.notes.join(' · ') : ''}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>

          {!record && (
            <div className="euf-apply">
              <label className="euf-check">
                <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />
                Email customers a shipping confirmation (no tracking number)
              </label>
              <div className="mk-actions">
                <button className="mk-btn-primary" disabled={!fulfillable} onClick={handleApply}>
                  Mark {fulfillable} order{fulfillable === 1 ? '' : 's'} fulfilled
                </button>
                <button className="euf-btn-secondary" onClick={reset}>Cancel</button>
              </div>
            </div>
          )}

          {record?.state === 'applying' && <p className="euf-note">Fulfilling in Shopify…</p>}
          {record?.state === 'error' && <div className="mk-error">Failed: {record.error}</div>}
          {record?.state === 'done' && res && (
            <div className="mk-result-card">
              <p><b className="status-ok">{res.fulfilled_orders}</b> orders / {res.units} units marked fulfilled
                {res.notify_customer ? ' — customers emailed' : ''}.</p>
              {res.failed.length > 0 && (
                <p className="status-critical">Failed: {res.failed.map(f => `${f.order} (${f.error})`).join('; ')}</p>
              )}
              {record.slack_error && <p className="status-warning">Slack summary not posted: {record.slack_error}</p>}
              <div className="mk-actions euf-again">
                <button className="euf-btn-secondary" onClick={reset}>Upload another file</button>
              </div>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <>
          <h2 className="mk-section-title">Recent uploads</h2>
          <div className="table-wrap">
            <table>
              <thead><tr><th>When</th><th>File</th><th>By</th><th>Result</th></tr></thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id}>
                    <td>{new Date(h.uploaded_at).toLocaleString()}</td>
                    <td>{h.file}</td>
                    <td>{h.uploaded_by}</td>
                    <td>{h.state === 'done'
                      ? `${h.fulfilled_orders} orders / ${h.units} units fulfilled${h.failed ? `, ${h.failed} failed` : ''}`
                      : h.state === 'previewed' ? 'Previewed only' : h.state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
